/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo } from 'react';

import { useLocation, useParams } from 'js/lib/useRouter';
import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import { CoachChat } from 'bundles/ai-coach-platform/components/patterns';
import { ChatEmptyState } from 'bundles/ai-coach-platform/components/patterns/chat/common';
import { sendMessage } from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import {
  getContinuousChatSessionStorageKey,
  persistentContinuousChatSessionId,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/sessionUtils';
import { firstNameFromFullName } from 'bundles/ai-course-coach/utils/nameParser';
import CourseraAiFooter from 'bundles/unified-common/components/coach-on-discovery/CourseraAiFooter';
import CourseraAiHeader from 'bundles/unified-common/components/coach-on-discovery/CourseraAiHeader';
import CourseraAiMessage from 'bundles/unified-common/components/coach-on-discovery/CourseraAiMessage';
import CourseraAiMessageLoader from 'bundles/unified-common/components/coach-on-discovery/CourseraAiMessageLoader';
import CourseraAiTemporaryStreamedMessage from 'bundles/unified-common/components/coach-on-discovery/CourseraAiTemporaryStreamedMessage';
import StyledCoachChatContainer from 'bundles/unified-common/components/coach-on-discovery/StyledCoachChatContainer';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

import _t from 'i18n!nls/xdp';

type Props = {
  headerRef: React.RefObject<HTMLSpanElement>;
};

const styles = {
  coachChat: css`
    padding: 0;

    .coach-message-client {
      display: flex;
      align-items: flex-end;
    }
  `,
};

const COACH_MODE_ID = 'XDP_CAREER_FIT_ASSISTANT_V3';
const COACH_INTENTION_ROUTER_ACTION_ID = 'TYPED_MESSAGE';

const TwoPanelConversationalDiscoveryChatContainer = ({ headerRef }: React.PropsWithChildren<Props>) => {
  const { coachOpen, contextConfig, isContextConfigLoading, setContextConfig } = useCoachState();
  const location = useLocation();
  const { productSlug } = useParams();
  const initialMessageSent = React.useRef(false);
  const { fullName: userName } = getUser();
  const userFirstName = userName ? firstNameFromFullName(userName) : null;
  const displayTitle = userFirstName ? _t('Hi, #{userFirstName}!', { userFirstName }) : _t('Hi, learner!');
  const displaySubtitle = _t('How can I help?');
  const label = _t('Is this right for me?');
  const isAuthenticated = isAuthenticatedUser();

  const sessionStorageId = useMemo(() => {
    return persistentContinuousChatSessionId.get(
      getContinuousChatSessionStorageKey(
        location,
        undefined,
        isAuthenticated ? `logged-in-${productSlug}` : `logged-out-${productSlug}`
      )
    );
  }, [location, productSlug, isAuthenticated]);

  useEffect(() => {
    if (!isContextConfigLoading && contextConfig && !initialMessageSent.current) {
      if (!sessionStorageId) {
        sendMessage({
          input: {
            message: label,
            config: contextConfig.config,
          },
          enableContinuousChat: true,
          enableTextStreaming: true,
        });
      }
      initialMessageSent.current = true;

      // Switch to router action for all subsequent messages
      setContextConfig({
        ...contextConfig,
        config: {
          ...contextConfig.config,
          coachModeId: COACH_MODE_ID,
          coachActionId: COACH_INTENTION_ROUTER_ACTION_ID,
        },
      });
    }
  }, [isContextConfigLoading, contextConfig, label, setContextConfig, sessionStorageId]);

  return (
    <React.Fragment>
      {coachOpen && (
        <StyledCoachChatContainer headerRef={headerRef} topOffset={64}>
          {isContextConfigLoading ? (
            <div>Loading...</div>
          ) : (
            <CoachChat
              chatSessionKey={isAuthenticated ? `logged-in-${productSlug}` : `logged-out-${productSlug}`}
              aria-label={_t('Ask Coursera chat container')}
              inputLabel={_t('Ask Coursera')}
              chatMessageListAriaLabel={_t('Ask Coursera chat messages')}
              additionalStyles={styles.coachChat}
              showSettings={false}
              mode={contextConfig.config.coachModeId}
              contextConfig={contextConfig}
              storeSessionId={true}
              emptyState={<ChatEmptyState title={displayTitle} subtitle={displaySubtitle} />}
              customHeader={<CourseraAiHeader />}
              renderCustomMessage={(props) => <CourseraAiMessage {...props} recommendedActionsLabel="" />}
              renderCustomMessageLoader={(props) => <CourseraAiMessageLoader {...props} />}
              renderCustomTemporaryStreamedMessage={(props) => <CourseraAiTemporaryStreamedMessage {...props} />}
              customFooter={<CourseraAiFooter />}
              enableTextStreaming={true}
              enableContinuousChat={true}
            />
          )}
        </StyledCoachChatContainer>
      )}
    </React.Fragment>
  );
};

export default TwoPanelConversationalDiscoveryChatContainer;
